import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaCrosshairs } from 'react-icons/fa';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './styles/mapbox-popup-fix.css';
import './map-cursor-mix.css';
import PhotoModal from './components/PhotoModal';

// Custom hook to manage photo modal state globally across map markers
function usePhotoModalState() {
  const [activeModalListing, setActiveModalListing] = useState(null);
  
  const showModal = (listing) => {
    setActiveModalListing(listing);
  };
  
  const hideModal = () => {
    setActiveModalListing(null);
  };
  
  return {
    activeModalListing,
    showModal,
    hideModal
  };
}

// Helper function to create popup HTML content exactly matching PropertyCard style
const createPopupHtml = (listing) => {
  // SVG for camera icon (Bootstrap Icons camera-fill, 12x12, white fill)
  const cameraIconSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="white" viewBox="0 0 16 16" style="margin-right: 4px;">
      <path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828-.828A2 2 0 0 1 3.172 4H2z"/>
      <path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0-4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
    </svg>
  `;

  let photoCountBadgeHtml = '';
  const publicPhotoCount = (listing?.raw?.Media && Array.isArray(listing.raw.Media))
    ? listing.raw.Media.filter(m => m && m.MediaURL && m.Permission && Array.isArray(m.Permission) && m.Permission.includes('Public') && m.MediaCategory === 'Photo').length
    : 0;
  if (publicPhotoCount > 0) {
    photoCountBadgeHtml = `
      <div style="position: absolute; bottom: 5px; right: 5px; background-color: rgba(0, 0, 0, 0.6); color: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; display: flex; align-items: center; z-index: 1; line-height: 1;">
        ${cameraIconSvg}
        ${publicPhotoCount}
      </div>
    `;
  }
  // Extract essential data with proper fallbacks
  const photoUrl = listing.photo || '';
  const price = listing.price ? `$${listing.price.toLocaleString()}` : 'Price unavailable';
  const beds = listing.beds || '--';
  const baths = listing.baths || '--';
  const sqft = listing.sqft ? listing.sqft.toLocaleString() : '--';
  const status = listing.status || 'Status';
  const lotSizeAcres = listing.lotSizeAcres || '--';
  const propertyType = listing.propertyType || '';
  
  // Determine if this is a lot or land listing
  const isLotOrLand = propertyType.toLowerCase().includes('lot') || propertyType.toLowerCase().includes('land');
  
  // Process address correctly
  const unparsed = listing.unparsedAddress || '';
  const streetAddress = (() => {
    // Try to extract from unparsed address first
    if (unparsed && listing.city) {
      const idx = unparsed.indexOf(listing.city);
      if (idx > 0) {
        return unparsed.substring(0, idx).trim().replace(/,$/, '');
      }
    }
    
    // Fallback: build from raw fields if needed
    if (listing.raw) {
      const rn = listing.raw;
      return [rn.StreetNumber, rn.StreetDirPrefix, rn.StreetName, rn.StreetSuffix, rn.UnitNumber]
        .filter(Boolean)
        .join(' ')
        .replace(/  +/g, ' ')
        .trim();
    }
    
    return unparsed || 'Address unavailable';
  })();
  
  // Format city, state, zip
  const cityStateZip = (() => {
    const city = listing.city || listing.raw?.City || '';
    const state = listing.state || listing.raw?.StateOrProvince || 'OR';
    const zip = listing.postalCode || listing.raw?.PostalCode || '';
    
    return [city, state, zip].filter(Boolean).join(', ').replace(/, ([0-9]{5})/, ' $1');
  })();
  
  // Get neighborhood or subdivision if available
  let neighborhood = '';
  if (listing.raw?.SubdivisionName && listing.raw.SubdivisionName !== 'N/A') {
    neighborhood = listing.raw.SubdivisionName;
  } else if (listing.city) {
    neighborhood = listing.city;
  } else if (listing.raw?.City) {
    neighborhood = listing.raw.City;
  }
  
  return `
    <div style="
      width: 280px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
      background-color: #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      font-family: 'Montserrat', Arial, system-ui, sans-serif;
    ">
      <div style="position: relative; width: 100%; padding-bottom: 65%; margin: 0; background: #f0f0f0; overflow: hidden; border-radius: 8px 8px 0 0;">
        ${photoUrl ? `
          <img 
            src="${photoUrl}" 
            alt="${streetAddress}" 
            style="
              position: absolute;
              width: 100%;
              height: 100%;
              object-fit: cover;
              top: 0;
              left: 0;
              cursor: pointer;
              margin: 0;
              padding: 0;
              border: none;
              display: block;
            "
            class="map-popup-photo"
          />` : 
          `<div style="position: absolute; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #888; font-size: 14px;">No image</div>`
        }
        <div style="
          position: absolute;
          top: 8px; /* Adjusted for better spacing */
          left: 8px; /* Adjusted for better spacing */
          padding: 4px 8px; /* Adjusted padding */
          border-radius: 4px;
          font-size: 11px; /* Adjusted font size */
          font-weight: 600;
          background-color: ${status === 'Active' ? '#4CAF50' : status === 'Pending' ? '#FFC107' : '#757575'};
          color: white; /* Explicitly white */
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2); /* Softer shadow */
          z-index: 1;
          line-height: 1.2; /* Adjusted line height */
        ">
          ${status}
        </div>
        ${photoCountBadgeHtml}
      </div>
      <div style="padding: 16px; display: flex; flex-direction: column; flex-grow: 1;">
        <div style="font-size: 22px; font-weight: 600; color: #333; margin-bottom: 8px; font-variant-numeric: tabular-nums;">
          ${price}
        </div>
        <div style="display: flex; align-items: center; color: #444; margin-bottom: 12px; height: 24px;">
          ${isLotOrLand ? `
            <div style="display: flex; align-items: center; font-size: 16px; color: #444;">
              ${lotSizeAcres !== '--' ? `<span><span style="font-weight: 600;">${lotSizeAcres}</span> acres</span>` : ''}
            </div>
          ` : `
            <div style="display: flex; align-items: center; font-size: 16px;">
              <span style="font-weight: 600;">${beds}</span>&nbsp;bed
              <span style="margin: 0 4px; color: #555;">·</span>
              <span style="font-weight: 600;">${baths}</span>&nbsp;bath
              <span style="margin: 0 4px; color: #555;">·</span>
              <span style="font-weight: 600;">${sqft}</span>&nbsp;sqft
            </div>
          `}
        </div>
        <div style="height: 44px; margin-bottom: 8px; overflow: hidden;">
          <div style="font-size: 16px; font-weight: 500; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${streetAddress}">
            ${streetAddress}
          </div>
          <div style="font-size: 14px; color: #666; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${cityStateZip}
          </div>
        </div>
        ${neighborhood ? `
          <div style="font-size: 14px; color: #555; display: flex; align-items: center; height: 24px; margin-top: 4px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 4px; flex-shrink: 0;">
              <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#555"/>
            </svg>
            <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${neighborhood}</span>
          </div>
        ` : ''}
      </div>
    </div>
  `;
};

// Set Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

function MapView({ listings, isMobile }) {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const originalMapViewRef = useRef(null);
  const mapViewRootRef = useRef(null); // Ref for the root div of MapView
  const mapDomContainerRef = useRef(null); // Ref for the actual map container div
  const { activeModalListing, showModal: originalShowModal, hideModal: originalHideModal } = usePhotoModalState();
  const isPhotoModalOpenRef = useRef(false);

  // Wrapped modal handlers to track modal state
  // A ref to store the exact camera state when modal opens
  const savedCameraStateRef = useRef(null);
  
  const showModal = useCallback((listing) => {
    // Save exact camera state before opening modal
    if (mapRef.current) {
      savedCameraStateRef.current = {
        center: mapRef.current.getCenter(),
        zoom: mapRef.current.getZoom(),
        bearing: mapRef.current.getBearing(),
        pitch: mapRef.current.getPitch()
      };
      
      // Set flag to indicate modal is opening
      isPhotoModalOpenRef.current = true;
      
      // Open the modal
      originalShowModal(listing);
      
      // Force map back to saved position in the next animation frame
      // This ensures it runs after any DOM updates
      requestAnimationFrame(() => {
        if (mapRef.current && savedCameraStateRef.current) {
          mapRef.current.jumpTo({
            center: savedCameraStateRef.current.center,
            zoom: savedCameraStateRef.current.zoom,
            bearing: savedCameraStateRef.current.bearing,
            pitch: savedCameraStateRef.current.pitch
          });
        }
      });
    } else {
      isPhotoModalOpenRef.current = true;
      originalShowModal(listing);
    }
  }, [originalShowModal]);

  const hideModal = useCallback(() => {
    // Hide the modal
    originalHideModal();
    
    // Force map back to saved position in the next animation frame
    requestAnimationFrame(() => {
      if (mapRef.current && savedCameraStateRef.current) {
        mapRef.current.jumpTo({
          center: savedCameraStateRef.current.center,
          zoom: savedCameraStateRef.current.zoom,
          bearing: savedCameraStateRef.current.bearing,
          pitch: savedCameraStateRef.current.pitch
        });
      }
    });
    
    // Only update the flag after ensuring map position
    isPhotoModalOpenRef.current = false;
  }, [originalHideModal]);

  const setupPopupPhotoClickListener = useCallback((popupInstance, listingForModal) => {
    const popupElement = popupInstance.getElement();
    if (!popupElement) return;
    const photoImg = popupElement.querySelector('img.map-popup-photo');
    if (photoImg && !photoImg.dataset.listenerAttached) {
      const clickHandler = (e) => {
        e.stopPropagation();
        showModal(listingForModal); // showModal is the wrapped one
      };
      photoImg.addEventListener('click', clickHandler);
      photoImg.dataset.listenerAttached = 'true'; // Mark as listener added
    }
  }, [showModal]); // Depends on the wrapped showModal
  

  // Initialize map
  useEffect(() => {
    // Check if map container exists
    const mapContainer = document.getElementById('full-map-view');
    if (!mapContainer) {
      console.error('Map container #full-map-view not found');
      return;
    }

    // Initialize map only if it hasn't been initialized yet
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapDomContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-121.3153, 44.0582],
      zoom: 10,
      attributionControl: true // Ensure default (full) attribution is used
    });

    // Add navigation controls (zoom, compass) - only on desktop
    if (!isMobile) {
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    }
    
    mapRef.current = map;
    console.log('Mapbox map initialized in MapView (default attribution):', map);

    // Add map click handler to close popups
    map.on('click', () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    });

    // Add custom attribution control

    // Cleanup on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [isMobile]);

  // Update markers when listings change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !listings?.length) return;
    
    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    // Create a bounds object to encompass all listings
    const bounds = new mapboxgl.LngLatBounds();
    
    // Create markers for listings with valid coordinates
    listings.forEach(listing => {
      const lat = parseFloat(listing.raw?.Latitude);
      const lon = parseFloat(listing.raw?.Longitude);
      if (isNaN(lat) || isNaN(lon)) return;

      // Extend bounds to include this listing
      bounds.extend([lon, lat]);

      let markerColor = '#003DA5';
      if (listing.status === 'Pending') markerColor = '#FFC107';
      else if (listing.status === 'Closed') markerColor = '#666666';
      else if (listing.status === 'Active') markerColor = '#4CAF50';

      const marker = new mapboxgl.Marker({ color: markerColor, scale: 0.8 })
        .setLngLat([lon, lat]);

      // Create popup with shared styling
      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: true,
        maxWidth: '280px',
        className: 'custom-mapbox-popup map-view-popup',
        closeOnMove: false
      })
        .setLngLat([lon, lat])
        .setHTML(createPopupHtml(listing, (clickedListing) => {
          showModal(clickedListing);
        }, 'normal'));
      
      // Handle popup close
      popup.on('close', () => {
        if (isMobile && originalMapViewRef.current && mapRef.current && !isPhotoModalOpenRef.current) {
          mapRef.current.easeTo({
            center: originalMapViewRef.current.center,
            zoom: originalMapViewRef.current.zoom,
            duration: 500,
            essential: true
          });
          originalMapViewRef.current = null; // Clear after use
        }
        if (popupRef.current === popup) {
          popupRef.current = null;
        }
      });

      // Add click handler to the marker to show the popup
      marker.getElement().addEventListener('click', (e) => {
        e.stopPropagation();
        
        // Close any existing popup
        if (popupRef.current) {
          popupRef.current.remove();
        }

        if (isMobile) {
          const addPopupAfterPan = () => {
            if (mapRef.current) {
              // Check if the popup instance we are about to show is the one from this click
              // This check is mostly for safety in case of rapid clicks, though popupRef.current logic outside should handle it.
              if (!popup.isOpen()) { // Add only if not already open (e.g. by another event)
                popupRef.current = popup; // Set the global ref to this popup
                popup.addTo(mapRef.current);
                setupPopupPhotoClickListener(popup, listing);
              }
              mapRef.current.off('moveend', addPopupAfterPan); // Clean up listener
            }
          };

          if (mapRef.current) {
            originalMapViewRef.current = {
              center: mapRef.current.getCenter(),
              zoom: mapRef.current.getZoom()
            };
            mapRef.current.once('moveend', addPopupAfterPan);
            // Calculate dynamic offset for mobile to ensure popup is visible below header
            const mobileEstimatedHeaderHeight = 90; // From Header.jsx inspection
            const mobilePopupAboveMarkerHeight = 0;  // No extra space for popup
            const mobileTopBuffer = -10;     // Negative value to position above header's bottom

            const targetMarkerScreenY = mobileEstimatedHeaderHeight + mobilePopupAboveMarkerHeight + mobileTopBuffer;
            
            if (mapDomContainerRef.current) {
              const viewportHeight = mapDomContainerRef.current.clientHeight;
              const calculatedOffsetY = targetMarkerScreenY - (viewportHeight / 2);

              mapRef.current.easeTo({
                center: [lon, lat],
                offset: [0, calculatedOffsetY], // Dynamically calculated Y-offset
                duration: 500,
                essential: true
              });
            } else {
              // Fallback if mapDomContainerRef is not available (should not happen)
              mapRef.current.easeTo({
                center: [lon, lat],
                offset: [0, -150], // A less aggressive fixed offset as a fallback
                duration: 500,
                essential: true
              });
            }
          }
        } else {
          // Not mobile, add popup immediately
          popupRef.current = popup; // Set the global ref to this popup
          popup.addTo(map);
          setupPopupPhotoClickListener(popup, listing);
        }
      });

      marker.addTo(map);
      markersRef.current.push(marker);
    });
    
    // If we have valid coordinates, fit the map to show all listings
    if (!bounds.isEmpty()) {
      const bottomPadding = isMobile ? 50 : 50; // 50px for mobile, 50px for desktop
      map.fitBounds(bounds, { 
        padding: { top: 50, bottom: bottomPadding, left: 60, right: 60 },
        maxZoom: 13, // Don't zoom in too far
        duration: 1000 // Smooth animation duration in ms
      });
    }
  }, [listings, showModal, isMobile, setupPopupPhotoClickListener]);

  // Effect to handle map resizing using ResizeObserver
  useEffect(() => {
    const map = mapRef.current;
    const mapContainerElement = mapViewRootRef.current; // The element being observed

    if (!map || !mapContainerElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      map.resize();
    });

    resizeObserver.observe(mapContainerElement);

    // Initial resize call in case the observer doesn't fire immediately or for initial setup
    // Wait for the next animation frame to ensure layout is stable
    requestAnimationFrame(() => {
      map.resize();
    });

    return () => {
      resizeObserver.unobserve(mapContainerElement);
      resizeObserver.disconnect();
    };
  }, []); // Runs once after initial mount and map/container refs are set

  const handleRecenterMap = () => {
    const map = mapRef.current;
    if (!map || !listings || listings.length === 0) {
      console.log('Recenter: No map or no listings to recenter.');
      return;
    }

    // Close any open popup before recentering
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    const bounds = new mapboxgl.LngLatBounds();
    let validPointsExist = false;
    listings.forEach(listing => {
      const lat = parseFloat(listing.raw?.Latitude);
      const lon = parseFloat(listing.raw?.Longitude);
      if (!isNaN(lat) && !isNaN(lon)) {
        bounds.extend([lon, lat]);
        validPointsExist = true;
      }
    });

    if (validPointsExist) {
      const padding = { top: 50, bottom: 50, left: 60, right: 60 }; // Consistent with initial load
      map.fitBounds(bounds, { 
        padding: padding,
        maxZoom: 13, 
        duration: 1000 
      });
    } else {
      console.log('Recenter: No valid points found in listings.');
    }
  };

  return (
    <>
      <div ref={mapViewRootRef} className="map-side" style={{
        position: 'relative', // For positioning the absolute map container
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden', // Important to contain the map
        boxSizing: 'border-box'
      }}>
        <div 
          ref={mapDomContainerRef}
          id="full-map-view" 
          className="map-container"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%', // Explicit width/height can be redundant but safe
            height: '100%'
          }}
        />
        <button
          onClick={e => { handleRecenterMap(); if (e.currentTarget) e.currentTarget.blur(); }}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 2, // Ensure it's above map tiles and other controls like zoom if they were top-right
            background: 'white',
            border: '1px solid #dbdbdb',
            borderRadius: '4px',
            padding: '8px',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: '1'
          }}
          title="Recenter Map"
        >
          <FaCrosshairs size={18} color="#333" />
        </button>
      </div>
      
      {/* Photo Modal - same component used in PropertyCard */}
      {activeModalListing && (
        <PhotoModal
          listing={activeModalListing}
          onClose={hideModal}
          isMobile={isMobile}
        />
      )}
    </>
  );
}

export default MapView;
